import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import firebase from '../config/firebase.config';
import _ from 'lodash';

const database = firebase.firestore();

const DEFAULT_HOLIDAYS = [];

/**
 * Custom hook to fetch and cache the company's current-year holiday list
 * from companyDatabase/{companyId}/holidays/{year}. Mirrors useLeavePolicy's
 * cache-first + onSnapshot pattern.
 *
 * @param {string} [overrideCompanyId] - When set (non-empty), read the list for this company instead of the user's active company.
 * @returns {{ holidays: { date: string, name: string, optional?: boolean, description?: string }[], loading: boolean }}
 */
export function useHolidays(overrideCompanyId) {
  const userProfile = useSelector((state) => state.auth.profile);
  const activeCompanyId = !_.isEmpty(userProfile?.activeCompany)
    ? userProfile?.activeCompany?.companyId
    : null;
  const companyId =
    typeof overrideCompanyId === 'string' && overrideCompanyId.trim() !== ''
      ? overrideCompanyId.trim()
      : activeCompanyId;

  const [holidays, setHolidays] = useState(DEFAULT_HOLIDAYS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) {
      setHolidays(DEFAULT_HOLIDAYS);
      setLoading(false);
      return;
    }

    const currentYear = new Date().getFullYear();
    const cacheKey = `holidays_${companyId}_${currentYear}`;
    const cachedValue = localStorage.getItem(cacheKey);

    if (cachedValue !== null) {
      try {
        setHolidays(JSON.parse(cachedValue));
        setLoading(false);
      } catch {
        // Ignore a malformed cache entry - the snapshot below will replace it.
      }
    }

    const holidaysRef = database
      .collection('companyDatabase')
      .doc(companyId)
      .collection('holidays')
      .doc(currentYear.toString());

    const unsubscribe = holidaysRef.onSnapshot(
      (doc) => {
        const resolved = doc.data()?.holidays ?? DEFAULT_HOLIDAYS;
        setHolidays(resolved);
        localStorage.setItem(cacheKey, JSON.stringify(resolved));
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching holidays:', error);
        if (cachedValue === null) {
          setHolidays(DEFAULT_HOLIDAYS);
        }
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [companyId]);

  return { holidays, loading };
}
